import { ReactElement, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useSidebarPinState } from '@backstage/core-components';
import {
  NavContentBlueprint,
  type NavContentNavItem,
} from '@backstage/plugin-app-react';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import {
  SearchModal,
  SearchModalProvider,
  useSearchModal,
} from '@backstage/plugin-search';
import { useUserProfile } from '@backstage/plugin-user-settings';
import {
  Avatar,
  Focusable,
  Link,
  Tooltip,
  TooltipTrigger,
  useBreakpoint,
} from '@backstage/ui';
import {
  RiAddCircleLine,
  RiArticleLine,
  RiEyeLine,
  RiFileAddLine,
  RiLayoutGridLine,
  RiNodeTree,
  RiNotification2Line,
  RiOrganizationChart,
  RiPuzzleLine,
  RiSearchLine,
  RiSidebarFoldLine,
  RiSidebarUnfoldLine,
} from '@remixicon/react';
import { SidebarLogo } from './SidebarLogo';
import { useNavCollapsed } from './state';
import './Nav.css';

/**
 * Remix icons, BUI's own set, for the pages we know — most plugins still hand
 * their page an MUI icon. A page not listed here keeps the one it came with.
 */
const ICONS: Record<string, ReactElement> = {
  'page:catalog': <RiLayoutGridLine />,
  'page:org-chart': <RiOrganizationChart />,
  'page:scaffolder': <RiAddCircleLine />,
  'page:api-docs': <RiPuzzleLine />,
  'page:catalog-graph': <RiNodeTree />,
  'page:techdocs': <RiArticleLine />,
  'page:catalog-import': <RiFileAddLine />,
  'page:app-visualizer': <RiEyeLine />,
};

/** The catalog sits at `/`, which every path starts with. */
function isCurrent(pathname: string, href: string) {
  if (href === '/') return pathname === '/';
  return pathname === href || pathname.startsWith(`${href}/`);
}

/**
 * On the rail the labels are gone, so each row names itself on hover.
 * `Focusable` is what hands the trigger's hover handlers to the row: BUI's
 * `Link` takes only the focus ones from a `TooltipTrigger` on its own.
 */
function WithTooltip(props: { title: string; children: JSX.Element }) {
  const collapsed = useNavCollapsed();
  if (!collapsed) return props.children;
  return (
    <TooltipTrigger delay={300}>
      <Focusable>{props.children}</Focusable>
      <Tooltip placement="right">{props.title}</Tooltip>
    </TooltipTrigger>
  );
}

function ItemContent(props: {
  icon: ReactNode;
  title: string;
  badge?: number;
}) {
  const { icon, title, badge } = props;
  return (
    <>
      <span className="atlas-nav__icon">{icon}</span>
      <span className="atlas-nav__label">{title}</span>
      {badge ? (
        <span
          className="atlas-nav__badge"
          aria-label={`${badge} непрочитанных`}
        >
          {badge > 99 ? '99+' : badge}
        </span>
      ) : null}
    </>
  );
}

function NavLink(props: {
  href: string;
  title: string;
  icon: ReactNode;
  badge?: number;
  onClick?: () => void;
}) {
  const { href, title, icon, badge, onClick } = props;
  const { pathname } = useLocation();
  return (
    <WithTooltip title={title}>
      <Link
        href={href}
        onClick={onClick}
        className="atlas-nav__item"
        aria-current={isCurrent(pathname, href) ? 'page' : undefined}
      >
        <ItemContent icon={icon} title={title} badge={badge} />
      </Link>
    </WithTooltip>
  );
}

function NavButton(props: {
  title: string;
  icon: ReactNode;
  onPress: () => void;
}) {
  return (
    <WithTooltip title={props.title}>
      <button type="button" className="atlas-nav__item" onClick={props.onPress}>
        <ItemContent icon={props.icon} title={props.title} />
      </button>
    </WithTooltip>
  );
}

/** Search stays the plugin's modal; only its trigger is ours. */
function SearchItem() {
  const { state, toggleModal } = useSearchModal();
  return (
    <>
      <NavButton title="Search" icon={<RiSearchLine />} onPress={toggleModal} />
      <SearchModal {...state} toggleModal={toggleModal} />
    </>
  );
}

/** Settings, with the signed-in person's picture (or initials) for an icon. */
function SettingsItem({ item }: { item: NavContentNavItem }) {
  const { profile, displayName } = useUserProfile();
  return (
    <NavLink
      href={item.href}
      title={item.title}
      icon={
        <Avatar
          src={profile.picture ?? ''}
          name={displayName || item.title}
          size="x-small"
          purpose="decoration"
        />
      }
    />
  );
}

/** Pin or unpin; on a narrow screen the rail is forced, so there is nothing to toggle. */
function PinToggle() {
  const { isPinned, toggleSidebarPinState } = useSidebarPinState();
  const { up } = useBreakpoint();
  if (!up('sm')) return null;
  return (
    <NavButton
      title={isPinned ? 'Свернуть' : 'Развернуть'}
      icon={isPinned ? <RiSidebarFoldLine /> : <RiSidebarUnfoldLine />}
      onPress={toggleSidebarPinState}
    />
  );
}

export const SidebarContent = NavContentBlueprint.make({
  params: {
    component: ({ navItems }) => {
      const settings = navItems.take('page:user-settings');
      const nav = navItems.withComponent(item => (
        <NavLink
          href={item.href}
          title={item.title}
          icon={ICONS[item.node.spec.id] ?? item.icon}
        />
      ));

      // Skipped items
      nav.take('page:search'); // Using search modal instead
      nav.take('page:notifications'); // Using NotificationsSidebarItem manually instead

      return (
        <nav className="atlas-nav" aria-label="Основная навигация">
          <SidebarLogo />
          <SearchModalProvider>
            <SearchItem />
          </SearchModalProvider>
          <hr className="atlas-nav__divider" />
          <div className="atlas-nav__scroll">
            {nav.take('page:catalog')}
            {nav.take('page:org-chart')}
            {nav.take('page:scaffolder')}
            <hr className="atlas-nav__divider" />
            {nav.rest({ sortBy: 'title' })}
          </div>
          <hr className="atlas-nav__divider" />
          <NotificationsSidebarItem
            renderItem={({ unreadCount, to, onClick }) => (
              <NavLink
                href={to}
                title="Notifications"
                icon={<RiNotification2Line />}
                badge={unreadCount}
                onClick={onClick}
              />
            )}
          />
          {nav.take('page:app-visualizer')}
          {settings && <SettingsItem item={settings} />}
          <PinToggle />
        </nav>
      );
    },
  },
});
